import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import { CustomToggleTone, FilterField, FilterType, UserRole, UserSortType, UserType } from 'src/app/models/enums';
import { ICustomToggleOptions, IFilterPanelValues } from 'src/app/models';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';
import { StorageService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import {
  IUsersFilters,
  USER_FILTER_KEYS,
  deserializeUsersFilters,
  hasAnyFilterKey,
  parseUsersFiltersFromStorage,
} from 'src/app/filters';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';

@Component({
  selector: 'app-user-search-bar',
  templateUrl: './user-search-bar.component.html',
  styleUrls: ['./user-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserSearchBarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private queryParamsSub?: Subscription;
  @Input() placeholder = '';
  @Input() searching = false;
  @Input() searchValue = '';
  @Output() addUserEventEmitter = new EventEmitter();
  @Output() addUserDriverEventEmitter = new EventEmitter();
  @Output() filter = new EventEmitter();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() sort = new EventEmitter();
  @Output() onChangePaginationSize = new EventEmitter<number>();
  @Output() changeUserTypeEmitter = new EventEmitter<UserType>();
  @Input() set userType(value: UserType) {
    this.userTypeSelected = value;
  }

  public filterOpened = false;
  public filtersApplied = 0;

  public rowsDisplayOpened = false;
  public rowsDisplayValue = APP.DEFAULT_PAGE_SIZE;
  public defaultRowsDisplay = APP.DEFAULT_PAGE_SIZE;

  public sortedOpened = false;
  public userSortTypeSelected: UserSortType = UserSortType.CREATION;
  public sortOptions: any[] = [];

  public userTypeSelected: UserType = UserType.Regular;
  public UserTypes = UserType;

  public images = images;
  readonly userTypeToggleOptions: ICustomToggleOptions<UserType> = [
    {
      value: UserType.Regular,
      image: images.suggestions.user,
      descriptionKey: 'users.userTypes.users',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: UserType.Driver,
      image: images.suggestions.driver,
      descriptionKey: 'users.userTypes.drivers',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  public TooltipPosition = TooltipPosition;

  public adminRole: UserRole;
  public filterTypes = FilterType;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  async ngOnInit() {
    this.getRole();
    this.applyPageSizeFromUrl();
    await this.applySortFromUrl();
    this.updateFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => {
      this.applyPageSizeFromUrl();
      this.applySortFromUrl();
      this.updateFilters();
    });
  }

  ngAfterViewInit(): void {
    this.initSelectorOptions();
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  filterFields(): FilterField[] {
    return [
      FilterField.CLIENTS,
      this.userTypeSelected === UserType.Driver ? FilterField.OPERATORS : FilterField.AREAS,
      FilterField.USER_STATUS,
    ];
  }

  onFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.USERS]!.toFilters(values));
    this.updateFilters();
  }

  private applyPageSizeFromUrl() {
    const sizeParam = this.activatedRoute.snapshot.queryParamMap.get('size');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    this.rowsDisplayValue =
      sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0
        ? sizeFromUrl
        : this.defaultRowsDisplay;
  }

  private applySortFromUrl() {
    const sortFromUrl = this.activatedRoute.snapshot.queryParamMap.get(
      'sort',
    ) as UserSortType | null;
    this.userSortTypeSelected = sortFromUrl ?? UserSortType.CREATION;
  }

  async getRole() {
    this.adminRole = await this.storageService.getRole();
  }

  initSelectorOptions() {
    Object.values(UserSortType).forEach((sortType: UserSortType) => {
      this.sortOptions.push({
        value: sortType,
        icon: images.sortOptions[
          sortType.toLowerCase() as keyof typeof images.sortOptions
        ],
        label: `${sortType.toLowerCase()}`,
      });
    });
  }

  async handleSort(newValue: UserSortType) {
    this.userSortTypeSelected = newValue;
    await this.storageService.setUserSortType(newValue);
    this.sort.emit();
  }

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  closeFilter() {
    this.filterOpened = false;
  }

  changeUserType() {
    this.userTypeSelected =
      this.userTypeSelected === UserType.Regular
        ? UserType.Driver
        : UserType.Regular;
    this.changeUserTypeEmitter.emit(this.userTypeSelected);
  }

  updateNumberOfElements(event: number) {
    this.rowsDisplayOpened = false;
    const n = event > 0 ? event : this.defaultRowsDisplay;
    this.storageService.setUsersPageSize(n);
    this.onChangePaginationSize.emit(event);
  }

  async updateFilters() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const filters: IUsersFilters | null = hasAnyFilterKey(
      params,
      USER_FILTER_KEYS,
    )
      ? deserializeUsersFilters(params)
      : parseUsersFiltersFromStorage(
          await this.storageService.getUsersFilters(),
        );
    if (filters)
      this.filtersApplied = this.utilsService.getFiltersCount(filters);
    else this.filtersApplied = 0;
  }
}
